import { useQuery, useQueryClient } from '@tanstack/react-query';

import { DocumentStatusResource } from '../../../../types';
import { useSubscribeToDocumentStatusUpdated } from '../../../../websocketApi';
import { useRequestConstructor } from '../../../hooks/useRequestConstructor';
import { unwrapNonPaginatedResponseDataAs } from '../../../logic/unwrapNonPaginatedResponseDataAs';
import { NonPaginatedResponse } from '../../../types/NonPaginatedResponse';
import { DOCUMENTS_BASE_PATH } from '../../constants/documentsBasePath';
import { updateQueryDataAtKey } from '../../logic/updateQueryDataAtKey';
import { getDocumentStatus } from '../../requests/getDocumentStatus';
import { DocumentParams } from '../../types/DocumentParams';

export const useDocumentStatus = (
  documentParams: DocumentParams,
  documentId: string,
) => {
  const { constructGet } = useRequestConstructor();
  const queryClient = useQueryClient();

  const queryKey = [
    DOCUMENTS_BASE_PATH,
    'status',
    documentId,
    documentParams.definition,
  ];

  const { data, refetch, ...rest } = useQuery<
    Awaited<ReturnType<typeof getDocumentStatus>>
  >({
    queryKey,
    queryFn: async () =>
      getDocumentStatus(constructGet, documentParams, documentId),
    enabled: Boolean(documentId && documentParams.definition),
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
  });

  useSubscribeToDocumentStatusUpdated(documentId, ({ status }) => {
    updateQueryDataAtKey<NonPaginatedResponse<DocumentStatusResource>>(
      queryClient,
      queryKey,
      { data: status },
    );
  });

  return {
    ...unwrapNonPaginatedResponseDataAs(data, refetch, 'documentStatus'),
    ...rest,
  };
};
